/**
 * What a battle is *for*. Three circles of sea, a points race, and a number
 * that ends the match before the clock does.
 *
 * Pure functions and the tuning constants, on the same terms as `damage.ts`
 * and `vision.ts`: no clock, no `Math.random`, no I/O. The arena owns the
 * state, calls `stepZone()` once a tick with the sea time it has just
 * advanced through, and books whatever comes back.
 *
 * ── Why zones at all ────────────────────────────────────────────────────────
 *
 * A ten-minute deathmatch on open water has one stable answer: both lines sit
 * at maximum range and trade. Nobody closes, because closing is strictly
 * worse than not closing. A zone is a place on the chart where being there is
 * worth something, which is the only thing that makes the risk of being there
 * a decision rather than a mistake.
 *
 * ── The layout ──────────────────────────────────────────────────────────────
 *
 * Where the rings are is the chart's business, not this file's: every island
 * layout in `islands.ts` carries its own rings and spawn lines, so a fjord can
 * string them along its channel and an atoll can put one in the lagoon. The
 * classic three — one in the middle of the arena and one either side of it, at
 * a quarter of `LINE_DISTANCE` — are `CLASSIC_ZONES` there. What this file
 * fixes is the ring size and the rules for taking one.
 *
 * `ZONE_RADIUS` is 600 m: wide enough that holding it is station-keeping
 * rather than parking on a pixel, and narrow enough that a ship inside it is
 * inside a destroyer's detection range of the ring's far edge — you cannot sit
 * in a zone and expect not to be found.
 *
 * ── Taking one ──────────────────────────────────────────────────────────────
 *
 * Presence, and only presence. Ships of one team inside the circle with no
 * enemy inside move the bar from 0 to 100; at 100 the zone is theirs and stays
 * theirs until somebody takes it back. An enemy inside *freezes* the bar
 * rather than reversing it, so a single destroyer cannot undo a minute of work
 * by touching the ring — it has to survive in there, alone, long enough to
 * push the bar back down. Flipping a held zone therefore costs twice a fresh
 * capture: the old team's hundred has to be walked back to zero before the new
 * team's climb begins. That decay is the reason a zone does not snap when the
 * last defender dies; there is a window in which the defenders can come back.
 *
 * `CAPTURE_PER_SECOND` is 5, so one ship alone takes a neutral zone in twenty
 * seconds. Extra hulls help with diminishing returns — the rate goes as the
 * square root of the number present, so a second ship cuts the time by three
 * tenths, and a fifth ship shaves barely a second off what four were already
 * doing. Sending the whole division to sit in one ring is meant to look like
 * the mistake it is.
 *
 * ── Winning ─────────────────────────────────────────────────────────────────
 *
 * A held zone pays `POINTS_PER_ZONE_PER_SECOND` (1.2) and a sinking pays
 * `POINTS_PER_KILL` (75). First team to `VICTORY_POINTS` (1000) ends the
 * battle where it stands; otherwise the higher score at time-out wins.
 *
 * Those three numbers are set against a ten-minute battle of six a side. A
 * team that holds one zone for the whole thing and sinks five enemies scores
 * 1.2 × 540 + 375 ≈ 1020 — it reaches the cap in the last minute, which is the
 * shape of a close match. A team holding two zones scores 2.4 a second and
 * gets there around the seven-minute mark, which is the shape of a match that
 * was decided. And a team that cannot hold anything still has 450 points of
 * sinkings available to it, so "kill them all instead" remains a plan rather
 * than a consolation. The intent is that most battles end at the cap and the
 * clock is the exception, not the rule.
 */
import type { Team } from './protocol';

/** Metres. A ring this wide is station-keeping, not parking on a pixel. */
export const ZONE_RADIUS = 600;
/** Metres of clear water kept between a ring's edge and the arena wall. */
export const ZONE_WALL_MARGIN = 400;

/** Progress per second added by exactly one ship, out of 100. */
export const CAPTURE_PER_SECOND = 5;
/** Ships contribute as `count ** CAPTURE_CROWD_EXPONENT`: the fifth hull barely registers. */
export const CAPTURE_CROWD_EXPONENT = 0.5;

/** Points the winner has to reach; whoever gets here first ends the battle. */
export const VICTORY_POINTS = 1000;
/** Points a team earns per second, per zone it holds. */
export const POINTS_PER_ZONE_PER_SECOND = 1.2;
/** Points for putting an enemy on the bottom. */
export const POINTS_PER_KILL = 75;

/**
 * Names a layout may give its rings. A closed list because the client names
 * them in the player's language (`battle.zones.name.<id>`).
 */
export type ZoneId = 'west' | 'centre' | 'east' | 'north' | 'south' | 'lagoon' | 'inner' | 'middle' | 'outer' | 'port';
export const ZONE_IDS: readonly ZoneId[] = ['west', 'centre', 'east', 'north', 'south', 'lagoon', 'inner', 'middle', 'outer', 'port'];

export interface ZoneLayout {
  id: ZoneId;
  x: number;
  z: number;
  radius: number;
  /**
   * The team whose line this ring is nearest, or `null` for one that is
   * nobody's. Nothing in the maths reads it; it is here so a client can say
   * "your zone" without recomputing the geometry.
   */
  near: Team | null;
  /**
   * How much of the mode's ring income this ring pays, default 1. Epicentre's
   * inner ring is worth more than its outer one; nothing else sets it.
   */
  weight?: number;
}

export interface ZoneState {
  id: ZoneId;
  /** Who scores from it. `null` while nobody has finished a capture. */
  owner: Team | null;
  /** The team `progress` belongs to — whose hundred is on the bar. */
  contender: Team | null;
  /** 0–100, always read against `contender`. */
  progress: number;
  /** The team moving the bar this tick, either way; `null` when it is still. */
  capturing: Team | null;
  /** Both teams have a hull inside, so the bar is frozen. */
  contested: boolean;
  /** Alive hulls of each team inside the circle, this tick. */
  present: Record<Team, number>;
}

export function createZoneStates(zones: readonly ZoneLayout[]): ZoneState[] {
  return zones.map((z) => ({
    id: z.id,
    owner: null,
    contender: null,
    progress: 0,
    capturing: null,
    contested: false,
    present: { A: 0, B: 0 },
  }));
}

/** Everything the capture maths needs about one ship. */
export interface ZoneOccupant {
  team: Team;
  x: number;
  z: number;
  /**
   * A wreck holds nothing. The arena steps the zones after the shells have
   * landed, so a ship that went down this tick is already `false` here.
   */
  alive: boolean;
}

/** The zone a point is inside, or `null` — a layout's rings never overlap. */
export function zoneAt(zones: readonly ZoneLayout[], x: number, z: number): ZoneLayout | null {
  for (const zone of zones) {
    const dx = x - zone.x;
    const dz = z - zone.z;
    if (dx * dx + dz * dz <= zone.radius * zone.radius) return zone;
  }
  return null;
}

/** True when the point is inside this ring. */
export function insideZone(zone: ZoneLayout, x: number, z: number): boolean {
  const dx = x - zone.x;
  const dz = z - zone.z;
  return dx * dx + dz * dz <= zone.radius * zone.radius;
}

/**
 * Progress per second from `count` hulls. Zero for nobody, and diminishing
 * from there: four ships do twice what one does, and the fifth adds a
 * rounding error.
 */
export function captureRate(count: number): number {
  if (count <= 0) return 0;
  return CAPTURE_PER_SECOND * Math.pow(count, CAPTURE_CROWD_EXPONENT);
}

/** What changed about a zone in one step, for the event feed. */
export interface ZoneChange {
  /** `owner` is not what it was — a capture completed, or a hold was broken. */
  ownerChanged: boolean;
  /** `capturing` is not what it was — somebody started, or stopped, working. */
  capturingChanged: boolean;
}

const NO_CHANGE: ZoneChange = { ownerChanged: false, capturingChanged: false };

/**
 * Advance one zone by `dt` seconds against the hulls in the arena. `zone` is
 * the ring's place on the chart and `state` its bar; the pair must be the
 * same ring, or nothing happens. Mutates `state` in place — the arena holds
 * one array of these for the whole match — and reports only what a client
 * would need to be told about.
 *
 * The order is the whole rule: count, freeze if both sides are in, otherwise
 * push the bar the way the one side present wants it. Ownership is exactly
 * "the bar reads 100 for you", so it is lost the instant an enemy starts
 * pushing it back, not when it reaches zero.
 */
export function stepZone(zone: ZoneLayout, state: ZoneState, occupants: Iterable<ZoneOccupant>, dt: number): ZoneChange {
  if (zone.id !== state.id || dt <= 0) return NO_CHANGE;

  let a = 0;
  let b = 0;
  for (const o of occupants) {
    if (!o.alive || !insideZone(zone, o.x, o.z)) continue;
    if (o.team === 'A') a++;
    else b++;
  }
  state.present.A = a;
  state.present.B = b;
  state.contested = a > 0 && b > 0;

  const wasOwner = state.owner;
  const wasCapturing = state.capturing;

  if (state.contested || (a === 0 && b === 0)) {
    // Frozen, or empty. An empty ring keeps whatever the sea last wrote on
    // it: a half-taken zone nobody is standing in is a job left unfinished,
    // not a job undone.
    state.capturing = null;
  } else {
    const team: Team = a > 0 ? 'A' : 'B';
    const rate = captureRate(a > 0 ? a : b) * dt;
    state.capturing = team;
    if (state.contender === null || state.contender === team) {
      state.contender = team;
      state.progress = Math.min(100, state.progress + rate);
    } else {
      // Somebody else's hundred is on the bar. Walk it back to nothing before
      // starting your own — which is what makes a held zone worth the hulls
      // it took to hold, and gives the defenders a window to come back into.
      state.progress -= rate;
      if (state.progress <= 0) {
        state.progress = 0;
        state.contender = team;
      }
    }
    state.owner = state.contender !== null && state.progress >= 100 ? state.contender : null;
  }

  if (state.owner === wasOwner && state.capturing === wasCapturing) return NO_CHANGE;
  return { ownerChanged: state.owner !== wasOwner, capturingChanged: state.capturing !== wasCapturing };
}

/**
 * Points a team earns this second from everything it currently holds. `rings`
 * are the layouts the states were made from, in the same order, for their
 * weights; left out, every ring pays the same.
 */
export function zoneIncome(
  zones: readonly ZoneState[],
  team: Team,
  perZonePerSecond = POINTS_PER_ZONE_PER_SECOND,
  rings?: readonly ZoneLayout[],
): number {
  let held = 0;
  for (let i = 0; i < zones.length; i++) {
    if (zones[i]!.owner !== team) continue;
    held += rings?.[i]?.weight ?? 1;
  }
  return held * perZonePerSecond;
}

export type TeamScores = Record<Team, number>;

export function createScores(): TeamScores {
  return { A: 0, B: 0 };
}

/** The team that has reached the cap, or `null`. Ties go to the higher score. */
export function scoreVictor(scores: TeamScores, cap = VICTORY_POINTS): Team | null {
  const a = scores.A >= cap;
  const b = scores.B >= cap;
  if (!a && !b) return null;
  if (a && b) return scores.A === scores.B ? null : scores.A > scores.B ? 'A' : 'B';
  return a ? 'A' : 'B';
}

/** Who is ahead on points, or `null` for a dead heat. */
export function scoreLeader(scores: TeamScores): Team | null {
  if (Math.floor(scores.A) === Math.floor(scores.B)) return null;
  return scores.A > scores.B ? 'A' : 'B';
}

/** The wire form of a zone. Public information: the rings are on everyone's chart. */
export interface ZoneSnapshot {
  id: ZoneId;
  owner: Team | null;
  contender: Team | null;
  /** 0–100, rounded for the wire. */
  progress: number;
  capturing: Team | null;
  contested: boolean;
}

export function zoneSnapshot(state: ZoneState): ZoneSnapshot {
  return {
    id: state.id,
    owner: state.owner,
    contender: state.contender,
    progress: Math.round(state.progress),
    capturing: state.capturing,
    contested: state.contested,
  };
}
